import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import type { TimeSeriesData } from '../api/dashboard.schemas';

interface ValidationsByDateChartProps {
  data: TimeSeriesData;
  loading?: boolean;
  error?: Error;
  height?: number;
  className?: string;
}

/**
 * ValidationsByDateChart Component
 * 
 * Renders a line chart showing validation trends over time using ECharts.
 * Displays time series data with smooth curves and area fill.
 */
export function ValidationsByDateChart({ 
  data, 
  loading = false, 
  error, 
  height = 300,
  className = ""
}: ValidationsByDateChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading || error) return;

    // Initialize chart instance
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;

    // Configure chart options for time series
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
        formatter: (params: any) => {
          if (Array.isArray(params) && params.length > 0) {
            const param = params[0];
            return `${param.name}<br/>${param.seriesName}: ${param.value.toLocaleString()}`;
          }
          return '';
        },
      },
      legend: {
        data: ['Validations by Date'],
        top: 10,
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '15%',
        bottom: data.labels.length > 14 ? '15%' : '10%', // More space for rotated labels
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: data.labels,
        axisLabel: {
          rotate: data.labels.length > 14 ? 45 : 0, // Only rotate if more than 14 days
          interval: data.labels.length > 31 ? 'auto' : 0, // Show all labels for short periods
          formatter: (value: string) => {
            // Parse date from API format (d/M/yyyy or dd/MM/yyyy)
            let date: Date;
            
            // Try to parse the date - handle both d/M/yyyy and dd/MM/yyyy formats
            if (value.includes('/')) {
              const parts = value.split('/');
              if (parts.length === 3) {
                // Assuming format is d/M/yyyy (day/month/year)
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
                const year = parseInt(parts[2], 10);
                date = new Date(year, month, day);
              } else {
                date = new Date(value);
              }
            } else {
              date = new Date(value);
            }
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
              return value; // Return original value if parsing fails
            }
            
            // Format date labels based on data length
            if (data.labels.length <= 7) {
              // For 7 days or less, show day name + date
              return date.toLocaleDateString('en-US', { 
                weekday: 'short',
                day: 'numeric'
              });
            } else if (data.labels.length <= 31) {
              // For up to 31 days, show month + day
              return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              });
            } else {
              // For longer periods, show month + day
              return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              });
            }
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Validations',
        nameLocation: 'middle',
        nameGap: 50,
        axisLabel: {
          formatter: (value: number) => {
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toString();
          },
        },
      },
      series: [
        {
          name: 'Validations by Date',
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: {
            width: 2,
          },
          showSymbol: false,
          areaStyle: {
            opacity: 0.3,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(59, 130, 246, 0.5)',
              },
              {
                offset: 1,
                color: 'rgba(59, 130, 246, 0.1)',
              },
            ]),
          },
          emphasis: {
            focus: 'series',
          },
          data: data.data,
          itemStyle: {
            color: '#3b82f6',
          },
        },
      ],
      responsive: true,
    };

    chart.setOption(option);

    // Handle resize
    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, loading, error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <Card className={`h-full flex flex-col ${className}`}>
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Validations by Date</CardTitle>
          </div>
          <CardDescription>Validation trends over time</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground">Loading chart...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`h-full flex flex-col ${className}`}>
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Validations by Date</CardTitle>
          </div>
          <CardDescription>Failed to load validation trends</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p>Unable to display chart</p>
            <p className="text-sm">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data.data || data.data.length === 0 || !data.labels || data.labels.length === 0) {
    return (
      <Card className={`h-full flex flex-col ${className}`}>
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Validations by Date</CardTitle>
          </div>
          <CardDescription>No validation data available</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p>No data to display</p>
            <p className="text-sm">Validation trends will appear when data is available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`h-full flex flex-col ${className}`}>
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Validations by Date</CardTitle>
        </div>
        <CardDescription>Validation trends over time</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-6 pt-0 min-h-0">
        <div 
          ref={chartRef} 
          style={{ height: '100%', width: '100%' }}
          className="min-h-0"
        />
      </CardContent>
    </Card>
  );
}